import { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Avatar,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  OpenInNew as LinkIcon,
} from "@mui/icons-material";
import { RTL } from "../../cache/CacheRtl";
import { uploadImage } from "../../services/MediaUploadService";
import { colors } from "../ui/colors";

const NewSupplementDialog = ({ open, onClose, onSubmit }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    imageUrl: "",
    purchaseLink: "",
  });

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const cdnUrl = await uploadImage(file);
      if (cdnUrl) {
        setForm(prev => ({ ...prev, imageUrl: cdnUrl }));
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
    setUploading(false);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onSubmit({
      name: form.name.trim(),
      description: form.description.trim(),
      imageUrl: form.imageUrl.trim(),
      purchaseLink: form.purchaseLink.trim(),
    });
    handleReset();
  };

  const handleReset = () => {
    setForm({ name: "", description: "", imageUrl: "", purchaseLink: "" });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <RTL>
        <DialogTitle>תוסף חדש</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* Image upload */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                src={form.imageUrl}
                sx={{ width: 64, height: 64, border: `1px solid ${colors.border}` }}
                variant="rounded"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                sx={{ fontSize: "12px", textTransform: "none" }}
              >
                {uploading ? "מעלה..." : "העלאת תמונה"}
              </Button>
            </Box>

            <TextField
              fullWidth
              label="שם התוסף"
              value={form.name}
              onChange={handleChange("name")}
              required
              size="small"
            />

            <TextField
              fullWidth
              label="תיאור"
              value={form.description}
              onChange={handleChange("description")}
              multiline
              rows={2}
              size="small"
            />

            <TextField
              fullWidth
              label="קישור לרכישה"
              value={form.purchaseLink}
              onChange={handleChange("purchaseLink")}
              placeholder="https://..."
              size="small"
              InputProps={{
                endAdornment: form.purchaseLink ? (
                  <IconButton size="small" onClick={() => window.open(form.purchaseLink, "_blank")}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>ביטול</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!form.name.trim() || uploading}
            sx={{ bgcolor: colors.primary, "&:hover": { bgcolor: colors.primaryDark } }}
          >
            צור תוסף
          </Button>
        </DialogActions>
      </RTL>
    </Dialog>
  );
};

export default NewSupplementDialog;
